/* eslint-disable max-len */
import {
  Component,
  Host,
  h,
  Prop,
  Watch,
  Event,
  EventEmitter,
  Element,
} from '@stencil/core';
import { versionComponent } from '../../global/inject-dependencies';
import { sortArrayIntoColumns } from '../../global/utility/sort-array-into-columns/sort-array-into-columns';
import { Link } from '../ui-link/ui-link';

// TODO: Use slots for list items
@Component({
  tag: 'ui-link-list',
  styleUrl: 'ui-link-list.scss',
  shadow: true,
})
export class UiLinkList {
  @Element() el: HTMLElement;

  /**
   * Title that appears before the link-list
   */
  @Prop() headline: string;

  /**
   * Defines the list style: either it is **standard** - vertical, **horizontal** and **anchor**. The default is the standard.
   */
  @Prop() listStyle:
    | 'standard'
    | 'horizontal-buttons'
    | 'anchor-links'
    | 'horizontal-tags' = 'standard';

  @Prop() standardIconOption: 'icon' | 'none' | 'auto' = 'none';

  @Prop() icon: string;

  /**
   * Array of the links displayed on the list.
   */
  @Prop({ mutable: true }) links!: Link[];

  /**
   * Specificies into how many collumns the list is divided into. The default is 1.
   */
  @Prop() columns = 1;

  @Prop({ attribute: 'links' }) utilityLinks: string;

  @Prop() variant: 'white';

  /**
   * Specifies if the link has an underline or not, if not defined, the default is for there to be an underline.
   */
  @Prop() decoration: 'none' | 'default' = 'default';

  @Watch('utilityLinks')
  utilityLinksChangeHandler(newValue: string) {
    this.links = newValue ? JSON.parse(newValue) : this.links;
  }

  /**
   * An element receives a click event when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element. `linkClick` fires after both the *mousedown* and *mouseup* events have fired, in that order.
   */
  @Event({ eventName: 'linkClick' }) linkClick: EventEmitter<{ link: Link }>;

  componentWillLoad() {
    this.utilityLinksChangeHandler(this.utilityLinks);
  }

  renderList() {
    return (
      <ul
        class={{
          linklist: true,
          'linklist--horizontal-buttons':
            this.listStyle === 'horizontal-buttons',
          'linklist--anchor': this.listStyle === 'anchor-links',
          'linklist--horizontal-tags': this.listStyle === 'horizontal-tags',
        }}
      >
        {this.renderListItemsFor(this.links)}
      </ul>
    );
  }

  getIcon(link) {
    if (this.listStyle === 'anchor-links') {
      return 'anchor-link';
    }
    return this.standardIconOption === 'icon' &&
      link.iconOption !== 'icon' &&
      link.iconOption !== 'none'
      ? this.icon
      : link.icon;
  }

  hasIcon(link: Link) {
    return (
      this.listStyle === 'anchor-links' ||
      (this.standardIconOption === 'icon' &&
        this.icon &&
        (link?.iconOption === 'auto' || link?.iconOption === 'icon')) ||
      link?.iconOption === 'icon'
    );
  }

  isTag() {
    return this.listStyle === 'horizontal-tags';
  }

  getUiPillTag(link: Link) {
    return <ui-pill>{link.title}</ui-pill>;
  }

  handleClick(link: Link) {
    window.open(link.src);
  }

  renderListItemsFor(links: Link[]) {
    return links.map((link) => (
      <li
        class={this.hasIcon(link) && link.style === 'text' ? 'icon' : ''}
        key={link.title}
      >
        {this.hasIcon(link) && link.style === 'text' && !link.icon && (
          <ui-icon
            icon={this.getIcon(link)}
            type="system"
            variant={this.variant}
          ></ui-icon>
        )}
        {
          <ui-link
            decoration={this.decoration}
            href={link.src}
            target={link.target}
            /* TODO: The stdandardIconOption is currently not final from concept side.
           This is the current wanted behaviour */
            button={
              this.listStyle === 'anchor-links'
                ? ''
                : (link.style
                    ?.replace('text', '')
                    .replace('button-', '') as any)
            }
            onClick={() => {
              this.linkClick.emit({ link });
            }}
            delay={link.delay}
            icon={
              link.style !== 'text' || (link.style === 'text' && link.icon)
                ? this.getIcon(link)
                : ''
            }
          >
            {this.isTag() ? this.getUiPillTag(link) : link.title}
          </ui-link>
        }
      </li>
    ));
  }

  renderColumns(columns) {
    return columns.map((columItems, idx) => (
      <ul
        key={idx}
        class={{
          linklist: true,
          'linklist--anchor': this.listStyle === 'anchor-links',
        }}
      >
        {this.renderListItemsFor(columItems)}
      </ul>
    ));
  }

  renderMultiColumnList() {
    return (
      <div
        class={{
          multicolumn: true,
          [`multicolumn--${this.columns}`]: true,
        }}
      >
        {this.renderColumns(sortArrayIntoColumns(this.columns, this.links))}
      </div>
    );
  }

  render() {
    return (
      <Host>
        {this.headline ? (
          <ui-headline level={4} iconVariant={this.variant}>
            {this.headline}
          </ui-headline>
        ) : null}
        {this.columns > 1 && this.listStyle !== 'horizontal-buttons'
          ? this.renderMultiColumnList()
          : this.renderList()}
      </Host>
    );
  }
}

versionComponent(UiLinkList);
